import { intl } from 'lang/intl'
import { subscribeKey } from 'valtio/utils'

import { devicesState, selectNewDevice } from '../../Devices/useDevices'
import { publishTrack, roomState, unpublishTrack } from '../webRTCRoom'
import { getIsClientSecondCam } from '../../global/blocs/roomInfo'
import { logMessage } from '../../Logging/log'
import { noticesProxy, showNotice } from '../../Layout/Notices/Notices'

const handleMediaPermissionError = (
  e: DOMException,
  sentryMessage: string,
  extra: Record<string, any> = {}
) => {
  let noticeMessage: string | undefined

  switch (e.name) {
    case 'NotReadableError':
      noticeMessage = intl.formatMessage({
        defaultMessage:
          'Could not access device. Make sure it is not being used by another application.',
        id: 'dlNTki',
      })
      break
    case 'NotAllowedError':
      noticeMessage = intl.formatMessage({
        defaultMessage:
          'Permission denied. Access to your device is blocked by either your browser or operating system.',
        id: 'PFGIZM',
      })
      break
    case 'AbortError':
      noticeMessage = intl.formatMessage({
        defaultMessage:
          'Your device failed to start. This may be a temporary issue, try again?',
        id: '3AE6ha',
      })
      break
    default:
      break
  }

  if (noticeMessage && noticesProxy.isShown) {
    showNotice({
      message: noticeMessage,
      expirationMillis: 5000,
      isClosable: true,
    })
  } else {
    logMessage(sentryMessage, {
      level: 'error',
      extra: {
        ...extra,
        cause: e,
        userMessage: noticeMessage,
      },
    })
  }
}

const updateVideoInputPublishedTrack = async () => {
  const localParticipant = roomState.participants.find((p) => p.isLocal)
  if (!localParticipant) return

  if (getIsClientSecondCam()) {
    const currentSelectedVideoInputId =
      devicesState.currentSelectedDevice.videoinput

    if (!currentSelectedVideoInputId) return

    await unpublishTrack('mobile')
    try {
      const videoStream = await navigator.mediaDevices.getUserMedia({
        video: {
          deviceId: currentSelectedVideoInputId,
          width: { ideal: 1920 },
          frameRate: { ideal: 30 },
        },
      })
      const videoTrack = videoStream.getVideoTracks()[0]
      if (videoTrack) publishTrack(videoTrack, 'mobile')
    } catch (e) {
      handleMediaPermissionError(
        e as DOMException,
        'Failed to grab media from mobile device',
        {
          deviceId: currentSelectedVideoInputId,
          videoDevices: devicesState.devices.videoinput,
        }
      )
    }
  } else {
    // ensure we're not republishing the same track
    const { videoinput: videoInputId } = devicesState.currentSelectedDevice

    if (videoInputId) {
      const currentTrackSettings =
        localParticipant.tracks.camera?.mediaStream?.getSettings()
      if (currentTrackSettings?.deviceId === videoInputId) return

      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({
          video: {
            deviceId: { exact: videoInputId },
            width: { ideal: 1920 },
            frameRate: { ideal: 30 },
          },
        })

        const videoTrack = videoStream.getVideoTracks()[0]
        if (videoTrack) publishTrack(videoTrack, 'camera')
      } catch (e) {
        handleMediaPermissionError(
          e as DOMException,
          'Failed to grab media from video device',
          {
            deviceId: videoInputId,
            videoDevices: devicesState.devices.videoinput,
          }
        )
      }
    } else {
      unpublishTrack('camera')
    }
  }
}

const updateAudioInputPublishedTrack = async () => {
  const localParticipant = roomState.participants.find((p) => p.isLocal)
  if (!localParticipant) return

  const { audioinput: audioInputId } = devicesState.currentSelectedDevice
  if (audioInputId) {
    const currentTrackSettings =
      localParticipant.tracks.cameraAudio?.mediaStream?.getSettings()
    if (currentTrackSettings?.deviceId === audioInputId) return

    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({
        audio: { deviceId: { exact: audioInputId } },
      })

      const audioTrack = audioStream.getAudioTracks()[0]
      if (audioTrack) publishTrack(audioTrack, 'cameraAudio')
    } catch (e) {
      handleMediaPermissionError(
        e as DOMException,
        'Failed to grab media from audio device',
        {
          deviceId: audioInputId,
          audioDevices: devicesState.devices.audioinput,
        }
      )
    }
  } else {
    unpublishTrack('cameraAudio')
  }
}

const restartMobileTrackWithNewFacingMode = async () => {
  const localParticipant = roomState.participants.find((p) => p.isLocal)
  if (!localParticipant) return
  await unpublishTrack('mobile')

  try {
    const videoStream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: { ideal: devicesState.mobileFacingMode },
        width: { ideal: 1920 },
        frameRate: { ideal: 30 },
      },
    })
    const videoTrack = videoStream.getVideoTracks()[0]

    selectNewDevice('videoinput', videoTrack.getSettings().deviceId)
  } catch (e) {
    handleMediaPermissionError(
      e as DOMException,
      'Failed to grab media from mobile device on facing mode change',
      {
        facingMode: devicesState.mobileFacingMode,
        videoDevices: devicesState.devices.videoinput,
      }
    )
  }
}

const updateVideoInputPublishedMobileTrack = async () => {
  const currentSelectedVideoInputId =
    devicesState.currentSelectedDevice.videoinput

  if (!currentSelectedVideoInputId) return

  try {
    const videoStream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: currentSelectedVideoInputId, width: { ideal: 1920 } },
    })

    const videoTrack = videoStream.getVideoTracks().at(0)
    if (videoTrack) {
      await publishTrack(videoTrack, 'mobile')
    }
  } catch (e) {
    handleMediaPermissionError(
      e as DOMException,
      'Failed to grab media from mobile device when recovering from background state.',
      {
        facingMode: devicesState.mobileFacingMode,
        videoDevices: devicesState.devices.videoinput,
      }
    )
  }
}

const updateDocumentVisibility = async () => {
  if (document.visibilityState === 'hidden') {
    // The app has been backgrounded. So, stop and unpublish track
    await unpublishTrack('mobile')
  } else {
    // The app has been foregrounded. So, publish track.´
    await updateVideoInputPublishedMobileTrack()
  }
}

/// ////////////////////////////// ///
/// initialization and destruction ///
/// ////////////////////////////// ///

const subscriptions: (() => void)[] = []

const init = () => {
  subscriptions.push(
    subscribeKey(
      devicesState.currentSelectedDevice,
      'videoinput',
      updateVideoInputPublishedTrack
    )
  )

  updateVideoInputPublishedTrack()

  // skip if it's a second cam
  if (getIsClientSecondCam()) {
    subscriptions.push(
      subscribeKey(
        devicesState,
        'mobileFacingMode',
        restartMobileTrackWithNewFacingMode
      )
    )

    document.addEventListener('visibilitychange', updateDocumentVisibility)
  } else {
    subscriptions.push(
      subscribeKey(
        devicesState.currentSelectedDevice,
        'audioinput',
        updateAudioInputPublishedTrack
      )
    )

    updateAudioInputPublishedTrack()
  }
}

const destroy = () => {
  while (subscriptions.length > 0) {
    subscriptions.shift()?.()
  }
}

export { init, destroy }
